import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORIES, MENU_ITEMS } from "@/data/menu";

export default function InteractiveMenu() {
    const [active, setActive] = useState("Tous");
    const items =
        active === "Tous" ? MENU_ITEMS : MENU_ITEMS.filter((i) => i.category === active);

    return (
        <section
            id="menu"
            data-testid="menu-section"
            className="relative bg-[#0d0d0d] py-24 sm:py-32"
        >
            <div className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[600px] -translate-x-1/2 rounded-full bg-rose-600/10 blur-[140px]" />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="font-mono2 mb-4 text-[11px] uppercase tracking-[0.3em] text-rose-400"
                >
                    La Carte — Prix nets, sans surprise
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl"
                >
                    Menu & <span className="text-rose-500">Tarifs</span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    data-testid="menu-homemade-note"
                    className="font-mono2 mt-5 inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-600/10 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-rose-400"
                >
                    Pain & frites faits maison, chaque jour
                </motion.p>

                <div
                    data-testid="menu-category-filter"
                    className="mt-10 flex flex-wrap gap-3"
                >
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat}
                            data-testid={`menu-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => setActive(cat)}
                            className={`font-mono2 rounded-full border px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
                                active === cat
                                    ? "border-rose-500 bg-rose-600 text-white glow-red"
                                    : "border-white/15 bg-transparent text-zinc-400 hover:border-rose-500/50 hover:text-rose-400"
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <motion.div layout className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                        {items.map((item) => (
                            <motion.article
                                layout
                                key={item.id}
                                data-testid="menu-item-card"
                                initial={{ opacity: 0, scale: 0.94 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                                whileHover={{ y: -6 }}
                                className="group overflow-hidden rounded-2xl border border-white/10 bg-[#18181B] transition-shadow duration-500 hover:shadow-[0_0_35px_rgba(185,28,28,0.25)]"
                            >
                                <div className="relative aspect-[16/10] overflow-hidden">
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] via-transparent to-transparent" />
                                    <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                                        {item.badges.map((b) => (
                                            <span
                                                key={b}
                                                data-testid="menu-item-badge"
                                                className="font-mono2 rounded-full border border-rose-500/40 bg-[#0A0A0A]/80 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-rose-400 backdrop-blur-sm"
                                            >
                                                {b}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-start justify-between gap-4">
                                        <h3 className="font-display text-lg font-black uppercase tracking-tight">
                                            {item.name}
                                        </h3>
                                        <span
                                            data-testid="menu-item-price"
                                            className="font-mono2 shrink-0 text-lg font-bold text-rose-500"
                                        >
                                            {item.price}
                                        </span>
                                    </div>
                                    <p className="font-mono2 mt-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                        {item.category}
                                    </p>
                                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                                        {item.description}
                                    </p>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
}
